import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog, Tab } from '@headlessui/react';
import {
  TicketIcon,
  PlusIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  SparklesIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  NoSymbolIcon,
  ArrowPathRoundedSquareIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function StatCard({ icon: Icon, label, value, color = 'blue', subtext }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
  };

  return (
    <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-surface-500 dark:text-surface-400">{label}</p>
          <p className="text-2xl font-bold text-surface-900 dark:text-white">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtext && (
            <p className="text-xs text-surface-400 dark:text-surface-500">{subtext}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function GenerateBatchModal({ isOpen, onClose, onSuccess, defaultPrefix }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    count: 10,
    prefix: defaultPrefix || 'BETA',
    batchName: '',
    maxUsesPerCode: 1,
    expiresAt: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await adminApi.generateBatchCodes({
        ...formData,
        expiresAt: formData.expiresAt || null
      });
      toast.success(`Generated ${result.data.batch.count} invite codes!`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate codes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-surface-800 rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6 border-b border-surface-200 dark:border-surface-700">
            <Dialog.Title className="text-xl font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <TicketIcon className="w-6 h-6 text-primary-500" />
              Generate Batch Codes
            </Dialog.Title>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Number of Codes
                </label>
                <input
                  type="number"
                  value={formData.count}
                  onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                  min="1"
                  max="1000"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Code Prefix
                </label>
                <input
                  type="text"
                  value={formData.prefix}
                  onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                  placeholder="BETA"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Batch Name
              </label>
              <input
                type="text"
                value={formData.batchName}
                onChange={(e) => setFormData({ ...formData, batchName: e.target.value })}
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                placeholder="e.g., January 2026 Launch"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Uses Per Code
                </label>
                <input
                  type="number"
                  value={formData.maxUsesPerCode}
                  onChange={(e) => setFormData({ ...formData, maxUsesPerCode: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Expires At
                </label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                rows={2}
                placeholder="Internal notes about this batch..."
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50">
                {loading ? 'Generating...' : 'Generate Codes'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function CreateIndividualModal({ isOpen, onClose, onSuccess, defaultPrefix }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    prefix: defaultPrefix || 'BETA',
    assignedEmail: '',
    maxUses: 1,
    expiresAt: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await adminApi.createIndividualCode({
        ...formData,
        expiresAt: formData.expiresAt || null
      });
      toast.success(`Created invite code: ${result.data.code.code}`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-surface-800 rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6 border-b border-surface-200 dark:border-surface-700">
            <Dialog.Title className="text-xl font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <PlusIcon className="w-6 h-6 text-primary-500" />
              Create Individual Code
            </Dialog.Title>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Code Prefix
              </label>
              <input
                type="text"
                value={formData.prefix}
                onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                placeholder="BETA"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Assign to Email (optional)
              </label>
              <input
                type="email"
                value={formData.assignedEmail}
                onChange={(e) => setFormData({ ...formData, assignedEmail: e.target.value })}
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                placeholder="user@example.com"
              />
              <p className="text-xs text-surface-500 mt-1">If set, only this email can use the code</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Max Uses
                </label>
                <input
                  type="number"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Expires At
                </label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white placeholder:text-surface-400"
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Code'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function CodeDetailModal({ code, isOpen, onClose }) {
  if (!code) return null;

  const copyCode = () => {
    navigator.clipboard.writeText(code.code);
    toast.success('Code copied to clipboard!');
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-surface-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-surface-200 dark:border-surface-700">
            <Dialog.Title className="text-xl font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <TicketIcon className="w-6 h-6 text-primary-500" />
              Invite Code Details
            </Dialog.Title>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4">
              <code className="text-lg font-mono font-bold text-primary-600 dark:text-primary-400">
                {code.code}
              </code>
              <button
                onClick={copyCode}
                className="p-2 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-lg transition-colors"
                title="Copy code"
              >
                <ClipboardDocumentIcon className="w-5 h-5 text-surface-500" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  code.status === 'active'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                    : code.status === 'used'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                      : code.status === 'expired'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                }`}>
                  {code.status?.charAt(0).toUpperCase() + code.status?.slice(1)}
                </span>
              </div>
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Type</p>
                <p className="font-medium text-surface-900 dark:text-white capitalize">{code.type}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Uses</p>
                <p className="font-medium text-surface-900 dark:text-white">{code.usedCount} / {code.maxUses}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Expires</p>
                <p className="font-medium text-surface-900 dark:text-white">
                  {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : 'Never'}
                </p>
              </div>
            </div>
            
            {code.assignedEmail && (
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Assigned To</p>
                <p className="font-medium text-surface-900 dark:text-white">{code.assignedEmail}</p>
              </div>
            )}
            
            {code.batchName && (
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Batch</p>
                <p className="font-medium text-surface-900 dark:text-white">{code.batchName}</p>
              </div>
            )}
            
            {code.notes && (
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400">Notes</p>
                <p className="text-surface-700 dark:text-surface-300">{code.notes}</p>
              </div>
            )}
            
            {code.usedBy && code.usedBy.length > 0 && (
              <div>
                <p className="text-sm text-surface-500 dark:text-surface-400 mb-2">Used By</p>
                <div className="space-y-2">
                  {code.usedBy.map((usage, idx) => (
                    <div key={idx} className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-3">
                      <p className="font-medium text-surface-900 dark:text-white">
                        {usage.companyName || usage.companyId?.name || 'Unknown Company'}
                      </p>
                      <p className="text-xs text-surface-500">
                        {new Date(usage.usedAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="text-xs text-surface-500 dark:text-surface-400">
              Created: {new Date(code.createdAt).toLocaleString()}
            </div>
          </div>
          
          <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function CodesTab() {
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [betaConfig, setBetaConfig] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);

  useEffect(() => {
    loadData();
  }, [statusFilter, typeFilter, searchQuery]);

  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      const [statsRes, codesRes, configRes] = await Promise.all([
        adminApi.getInviteCodeStats(),
        adminApi.getInviteCodes({
          page,
          limit: 20,
          status: statusFilter || undefined,
          type: typeFilter || undefined,
          search: searchQuery || undefined
        }),
        adminApi.getBetaConfig()
      ]);

      setStats(statsRes.data);
      setCodes(codesRes.data.codes || []);
      setPagination({
        page: codesRes.data.page,
        pages: codesRes.data.pages,
        total: codesRes.data.total
      });
      setBetaConfig(configRes.data.config);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const handleDeactivate = async (codeId) => {
    if (!window.confirm('Are you sure you want to deactivate this code?')) return;
    
    try {
      await adminApi.deactivateInviteCode(codeId);
      toast.success('Code deactivated');
      loadData(pagination.page);
    } catch (error) {
      toast.error('Failed to deactivate code');
    }
  };

  const handleReactivate = async (codeId) => {
    try {
      await adminApi.reactivateInviteCode(codeId);
      toast.success('Code reactivated');
      loadData(pagination.page);
    } catch (error) {
      toast.error('Failed to reactivate code');
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminApi.exportInviteCodes({
        status: statusFilter || undefined,
        type: typeFilter || undefined
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invite-codes-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export codes');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            icon={TicketIcon}
            label="Total Codes"
            value={stats.inviteCodes?.totalCodes || 0}
            color="blue"
          />
          <StatCard
            icon={CheckCircleIcon}
            label="Available"
            value={stats.inviteCodes?.availableCodes || 0}
            color="emerald"
          />
          <StatCard
            icon={UserGroupIcon}
            label="Used"
            value={stats.inviteCodes?.totalUses || 0}
            color="purple"
          />
          <StatCard
            icon={ClockIcon}
            label="Expired"
            value={stats.inviteCodes?.expiredCodes || 0}
            color="amber"
          />
          <StatCard
            icon={SparklesIcon}
            label="Beta Users"
            value={stats.beta?.totalBetaUsers || 0}
            color="blue"
            subtext={`${stats.beta?.conversionRate || 0}% converted`}
          />
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setShowBatchModal(true)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Generate Batch
            </button>
            <button
              onClick={() => setShowIndividualModal(true)}
              className="px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Create Individual
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Export CSV
            </button>
          </div>
          
          <div className="flex gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
              <input
                type="text"
                placeholder="Search codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 w-48 text-surface-900 dark:text-white placeholder:text-surface-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
              <option value="deactivated">Deactivated</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-surface-900 dark:text-white"
            >
              <option value="">All Types</option>
              <option value="batch">Batch</option>
              <option value="individual">Individual</option>
            </select>
            <button
              onClick={() => loadData()}
              className="p-2 text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : codes.length === 0 ? (
          <div className="p-12 text-center">
            <TicketIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
            <p className="text-surface-600 dark:text-surface-400">No invite codes found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-50 dark:bg-surface-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Uses
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                  {codes.map((code) => (
                    <tr key={code._id} className="hover:bg-surface-50 dark:hover:bg-surface-900/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded">
                            {code.code}
                          </code>
                          <button
                            onClick={() => handleCopyCode(code.code)}
                            className="p-1 hover:bg-surface-200 dark:hover:bg-surface-700 rounded transition-colors"
                            title="Copy code"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4 text-surface-400" />
                          </button>
                        </div>
                        {code.assignedEmail && (
                          <p className="text-xs text-surface-500 mt-1">{code.assignedEmail}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-surface-700 dark:text-surface-300 capitalize">
                          {code.type}
                        </span>
                        {code.batchName && (
                          <p className="text-xs text-surface-500">{code.batchName}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          code.status === 'active'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                            : code.status === 'used'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                              : code.status === 'expired'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {code.status?.charAt(0).toUpperCase() + code.status?.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-surface-700 dark:text-surface-300">
                        {code.usedCount} / {code.maxUses}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-surface-500">
                        {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedCode(code)}
                            className="p-1.5 text-surface-500 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          {code.status === 'active' && (
                            <button
                              onClick={() => handleDeactivate(code._id)}
                              className="p-1.5 text-surface-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                              title="Deactivate"
                            >
                              <NoSymbolIcon className="w-5 h-5" />
                            </button>
                          )}
                          {code.status === 'deactivated' && (
                            <button
                              onClick={() => handleReactivate(code._id)}
                              className="p-1.5 text-surface-500 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                              title="Reactivate"
                            >
                              <ArrowPathRoundedSquareIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-surface-200 dark:border-surface-700 flex items-center justify-between">
                <p className="text-sm text-surface-600 dark:text-surface-400">
                  Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadData(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadData(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="px-3 py-1 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <GenerateBatchModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSuccess={() => loadData()}
        defaultPrefix={betaConfig?.codePrefix}
      />
      
      <CreateIndividualModal
        isOpen={showIndividualModal}
        onClose={() => setShowIndividualModal(false)}
        onSuccess={() => loadData()}
        defaultPrefix={betaConfig?.codePrefix}
      />
      
      <CodeDetailModal
        code={selectedCode}
        isOpen={!!selectedCode}
        onClose={() => setSelectedCode(null)}
      />
    </div>
  );
}

function BetaUsersTab() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        adminApi.getBetaUsers({
          page,
          limit: 20,
          status: statusFilter || undefined
        }),
        adminApi.getInviteCodeStats()
      ]);

      setUsers(usersRes.data.companies || []);
      setPagination({
        page: usersRes.data.page,
        pages: usersRes.data.pages,
        total: usersRes.data.total
      });
      setStats(statsRes.data.beta);
    } catch (error) {
      console.error('Error loading beta users:', error);
      toast.error('Failed to load beta users');
    } finally {
      setLoading(false);
    }
  };

  const getBetaStatus = (company) => {
    const now = new Date();
    if (company.betaPlanUpgradedTo) return 'upgraded';
    if (company.betaTrialEndDate && new Date(company.betaTrialEndDate) > now) return 'trial_active';
    if (company.betaGracePeriodEndDate && new Date(company.betaGracePeriodEndDate) > now) return 'grace_period';
    return 'expired';
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            icon={UserGroupIcon}
            label="Total Beta Users"
            value={stats.totalBetaUsers}
            color="blue"
          />
          <StatCard
            icon={ClockIcon}
            label="Active Trials"
            value={stats.activeTrials}
            color="emerald"
          />
          <StatCard
            icon={ClockIcon}
            label="Grace Period"
            value={stats.inGracePeriod}
            color="amber"
          />
          <StatCard
            icon={CheckCircleIcon}
            label="Upgraded"
            value={stats.upgraded}
            color="purple"
            subtext={`Pro: ${stats.upgradedToPro}, Business: ${stats.upgradedToBusiness}`}
          />
          <StatCard
            icon={ChartBarIcon}
            label="Conversion Rate"
            value={`${stats.conversionRate}%`}
            color="blue"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-4">
        <div className="flex items-center gap-4">
          <FunnelIcon className="w-5 h-5 text-surface-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="trial_active">Active Trial</option>
            <option value="grace_period">Grace Period</option>
            <option value="upgraded">Upgraded</option>
            <option value="expired">Expired</option>
          </select>
          <button
            onClick={() => loadData()}
            className="p-2 text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <UserGroupIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
            <p className="text-surface-600 dark:text-surface-400">No beta users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-50 dark:bg-surface-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                    Invite Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                    Trial Ends
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                    Plan
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                {users.map((company) => {
                  const status = getBetaStatus(company);
                  return (
                    <tr key={company._id} className="hover:bg-surface-50 dark:hover:bg-surface-900/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-surface-900 dark:text-white">
                          {company.name}
                        </div>
                        <div className="text-sm text-surface-500">
                          {company.createdBy?.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm font-mono text-primary-600 dark:text-primary-400">
                          {company.betaInviteCode}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          status === 'trial_active'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                            : status === 'upgraded'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
                              : status === 'grace_period'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {status === 'trial_active' ? 'Active Trial' :
                           status === 'upgraded' ? 'Upgraded' :
                           status === 'grace_period' ? 'Grace Period' : 'Expired'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-surface-500">
                        {company.betaTrialEndDate
                          ? new Date(company.betaTrialEndDate).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-surface-700 dark:text-surface-300 capitalize">
                          {company.betaPlanUpgradedTo || company.billingPlan}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InviteCodes() {
  const tabs = [
    { name: 'Invite Codes', icon: TicketIcon, component: CodesTab },
    { name: 'Beta Users', icon: UserGroupIcon, component: BetaUsersTab }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
          <TicketIcon className="w-8 h-8 text-primary-500" />
          Invite Codes
        </h1>
        <p className="text-surface-400 mt-1">Manage beta invite codes and track beta users</p>
      </div>

      <Tab.Group>
        <Tab.List className="flex gap-1 bg-surface-800 p-1 rounded-lg w-fit">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all',
                  selected
                    ? 'bg-primary-500 text-white'
                    : 'text-surface-400 hover:text-surface-100 hover:bg-surface-700'
                )
              }
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-6">
          {tabs.map((tab) => (
            <Tab.Panel key={tab.name}>
              <tab.component />
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
